import { z } from 'zod';

// Shared revenue-analytics contract. A revenue event is a normal Logspot event
// with its monetary `value` populated (major units, e.g. dollars) plus a
// `currency` (ISO-4217) and optional `external_id` (source transaction id). The
// reserved event name (default 'Payment', project-configurable) marks an event
// as revenue so it is identifiable/filterable.

// ── Default revenue event name ────────────────────────────
// A label the SDK's `revenue()` helper emits; revenue is recognized by value + currency.
export const DEFAULT_REVENUE_EVENT_NAME = 'Payment';

// ── Currency ──────────────────────────────────────────────
// ISO-4217 alphabetic code, exactly three uppercase letters. We store the code
// verbatim with the amount; V1 does NO FX conversion. Project `baseCurrency`
// (default 'USD') is the display currency.
export const DEFAULT_BASE_CURRENCY = 'USD';

const ISO_4217_REGEX = /^[A-Z]{3}$/;

/**
 * Normalize a client-supplied currency to the canonical ISO-4217 shape
 * (uppercase, trimmed). Returns null when the input is absent or not a valid
 * 3-letter code — callers decide whether that is an error or a fallback to the
 * project base currency.
 */
export const normalizeCurrency = (input: unknown): string | null => {
  if (typeof input !== 'string') return null;
  const code = input.trim().toUpperCase();
  return ISO_4217_REGEX.test(code) ? code : null;
};

export const currencyCodeSchema = z
  .string()
  .trim()
  .transform((value) => value.toUpperCase())
  .refine((value) => ISO_4217_REGEX.test(value), {
    message: 'currency must be a 3-letter ISO-4217 code (e.g. USD)',
  });

// ── Revenue input (server-validated) ──────────────────────
// The generic revenue path accepts `value` + `currency` on the event pipeline.
// `value` is in MAJOR units and must be >= 0 on the generic/positive path —
// refunds use a dedicated negative path (the server owns that distinction), so
// the public DTO never accepts a negative amount directly. The server is the
// source of truth for this validation; the SDK is a thin wrapper.
export const revenueInputSchema = z.object({
  value: z
    .number()
    .refine((value) => Number.isFinite(value), {
      message: 'value must be a finite number',
    })
    .refine((value) => value >= 0, { message: 'value must be >= 0' }),
  currency: currencyCodeSchema.optional(),
  external_id: z.string().max(255).optional(),
});
export type RevenueInput = z.infer<typeof revenueInputSchema>;

// ── Reserved revenue metadata keys ────────────────────────
// Product/plan descriptors carried in event metadata for breakdowns. These are
// reserved keys the Stripe Connect path and `revenue()` helper populate.
export const REVENUE_METADATA_KEYS = {
  product: 'product',
  plan: 'plan',
  interval: 'interval',
  quantity: 'quantity',
} as const;
export type RevenueMetadataKey =
  (typeof REVENUE_METADATA_KEYS)[keyof typeof REVENUE_METADATA_KEYS];

// One-time vs recurring revenue.
export const REVENUE_INTERVALS = ['one_time', 'recurring'] as const;
export type RevenueInterval = (typeof REVENUE_INTERVALS)[number];

// ── Attribution model ─────────────────────────────────────
// Reports can be broken down by the source that ACQUIRED the entity
// (first-touch, durable) or the entity's MOST-RECENT source (last-touch). V1 is
// first + last only; multi-touch is deferred.
export const REVENUE_ATTRIBUTION_MODELS = [
  'first_touch',
  'last_touch',
] as const;
export type RevenueAttributionModel =
  (typeof REVENUE_ATTRIBUTION_MODELS)[number];

// ── Attribution snapshot (stamped onto a revenue event) ───
// Point-in-time copy of the identity's and account's first/last-touch source,
// written ONTO the revenue event at ingest (Phase 2) so historical reports stay
// correct even after the entity's current attribution changes. Defined here so
// the ingest path and report queries share one immutable shape. All fields are
// optional/nullable — attribution is best-effort and may be absent
// (unattributed revenue still counts toward totals).
export type AttributionTouchFields = {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  term?: string | null;
  content?: string | null;
  referrer?: string | null;
  channel?: string | null;
  landing_url?: string | null;
  at?: string | null;
};

export type RevenueAttributionSnapshot = {
  // Identity-level first/last touch.
  identityFirstTouch?: AttributionTouchFields | null;
  identityLastTouch?: AttributionTouchFields | null;
  // Account-level (company/group) first/last touch.
  accountFirstTouch?: AttributionTouchFields | null;
  accountLastTouch?: AttributionTouchFields | null;
};

// Reserved metadata key prefixes for the flattened attribution snapshot carried
// in event metadata (per the PRD appendix). Phase 2 writes/reads these; defined
// here so the writer and report queries never drift.
export const ATTRIBUTION_METADATA_PREFIXES = {
  identityFirstTouch: 'ft_',
  identityLastTouch: 'lt_',
  accountFirstTouch: 'acct_ft_',
  accountLastTouch: 'acct_lt_',
} as const;

// How a Stripe payment was tied to a person, strongest signal first: checkout
// metadata (identity id, user id, anonymous id), then the Stripe customer seen
// on an earlier payment, then the customer's email.
export const REVENUE_MATCH_METHODS = [
  'identity_id',
  'user_id',
  'anonymous_id',
  'stripe_customer',
  'email',
] as const;
export type RevenueMatchMethod = (typeof REVENUE_MATCH_METHODS)[number];

export const REVENUE_MATCH_METHOD_LABELS: Record<RevenueMatchMethod, string> = {
  identity_id: 'Identity ID',
  user_id: 'User ID',
  anonymous_id: 'Visitor ID',
  stripe_customer: 'Repeat Customer',
  email: 'Email',
};

export const REVENUE_MATCHED_BY_KEY = '_matched_by';

// Reserved internal metadata keys the platform stamps onto events: the refund
// reference, the Stripe PaymentIntent passthrough, how a Stripe payment was
// matched, the converting-session id, and the synthetic demo seeder's row tag.
// These are infrastructure, not user-defined properties, so they're hidden from
// BOTH the queries-page property picker and the event metadata view. NOTE: the
// REVENUE_METADATA_KEYS (`product`, `plan`, `interval`, `quantity`) are business
// dimensions customers also send themselves, so they are NOT reserved.
export const RESERVED_METADATA_KEYS: ReadonlySet<string> = new Set([
  '_refund_of',
  '_stripe_pi',
  REVENUE_MATCHED_BY_KEY,
  'logspot_session_id',
  'seed_tag',
]);

// Reserved key PREFIXES — the flattened first/last-touch attribution snapshot
// (ft_/lt_/acct_ft_/acct_lt_) written onto revenue events. Hidden alongside the
// keys above.
export const RESERVED_METADATA_PREFIXES: readonly string[] = [
  ATTRIBUTION_METADATA_PREFIXES.identityFirstTouch,
  ATTRIBUTION_METADATA_PREFIXES.identityLastTouch,
  ATTRIBUTION_METADATA_PREFIXES.accountFirstTouch,
  ATTRIBUTION_METADATA_PREFIXES.accountLastTouch,
];

// True when a metadata key is a user-facing property (safe to show in the
// property picker and event views), false for the platform's internal plumbing.
// Single source of truth shared by the backend (property picker + ingest) and
// the admin event feed so the two never drift.
export const isUserFacingMetadataKey = (key: string): boolean =>
  !RESERVED_METADATA_KEYS.has(key) &&
  !RESERVED_METADATA_PREFIXES.some((prefix) => key.startsWith(prefix));

// Remove the reserved/internal keys from client metadata before merging it with
// the server's data. Otherwise a caller with only the public key could set
// those keys directly to forge attribution or tamper with refund matching.
export const stripReservedMetadata = <T>(
  metadata: Record<string, T> | undefined | null,
): Record<string, T> | undefined => {
  if (!metadata) return undefined;
  const cleaned: Record<string, T> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (isUserFacingMetadataKey(key)) cleaned[key] = value;
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

// ── Report / metric DTOs (wire contract, snake_case) ──────
// Consumed by the Phase 4 revenue dashboard. Defined now so the report queries
// and the admin clients share one contract.
export type RevenueTimeseriesPoint = {
  day: string;
  revenue: number;
  orders: number;
};

export type RevenueAccountRow = {
  company_id: string;
  company_name: string | null;
  revenue: number;
  orders: number;
};

// One individual revenue event for the transactions list. `value` is negative
// for refunds; `company_id`/`identity_id` are null for unattributed events.
export type RevenueTransactionRow = {
  time: string;
  value: number;
  currency: string;
  identity_id: string | null;
  company_id: string | null;
  company_name: string | null;
  product: string | null;
  external_id: string | null;
  /** How a Stripe payment was matched to a person; null when not matched or not from Stripe. */
  matched_by: string | null;
};

export type RevenueByDimensionRow = {
  dimension: string;
  revenue: number;
  orders: number;
};

export type RevenueMetrics = {
  // Per-currency totals; V1 never blends currencies (no FX). Keyed by ISO-4217.
  total_revenue: number;
  orders: number;
  // Distinct attributed accounts in the window (the ARPA denominator).
  accounts: number;
  // Average order value = total_revenue / orders.
  aov: number;
  // Revenue per account (ARPA) and per active user (ARPU).
  arpa: number;
  arpu: number;
  currency: string;
  // Payments in the window sent without a currency, left out of the totals above.
  payments_without_currency: number;
};

// LTV cohort row: lifetime revenue per entity (account or identity) grouped by
// the entity's first-seen month. `ltv` = revenue / entities for the cohort.
export type RevenueLtvCohort = {
  cohort: string;
  entities: number;
  revenue: number;
  orders: number;
  ltv: number;
};
